import type { Limrun } from "@limrun/api";
import { orgRequestOptions } from "@/client/director/org";
import { installAndLaunchApp, installDeviceApp, launchDeviceApp, restoreDeviceSnapshot, LegacySnapshotAppsRequiredError, type DeviceStateStatus, openDeviceUrl } from "./device-operations";
import { parsePlaygroundPreview, playgroundPreviewUrl, type PlaygroundConfiguration } from "./playground-configuration";

export type DeviceSetup = {
  configuration: PlaygroundConfiguration;
  organizationId: string;
  nextAsset: number;
  snapshotPending?: boolean;
};

// Store names and launch settings, never instance tokens or expiring download URLs.
const storageKey = (instanceId: string) => `console:device-setup:${instanceId}`;

export function readDeviceSetup(instanceId?: string): DeviceSetup | undefined {
  if (!instanceId) return;
  try {
    const stored = sessionStorage.getItem(storageKey(instanceId));
    if (!stored) return;
    const { query, nextAsset, snapshotPending } = JSON.parse(stored);
    const parsed = parsePlaygroundPreview(new URLSearchParams(query));
    if (!parsed.organizationId || !Number.isInteger(nextAsset) || nextAsset < 0 || nextAsset > parsed.configuration.assets.length ||
      !instanceId.startsWith(`${parsed.configuration.platform}_`)) throw new Error("Invalid device setup checkpoint");
    return { ...parsed, organizationId: parsed.organizationId, nextAsset, ...(snapshotPending === true && { snapshotPending }) };
  } catch (error) {
    console.warn("Could not restore device setup progress", error);
  }
}

export function writeDeviceSetup(instanceId: string, setup?: DeviceSetup): boolean {
  try {
    if (setup) {
      const query = new URL(playgroundPreviewUrl(setup.configuration, setup.organizationId, window.location.origin)).search;
      sessionStorage.setItem(storageKey(instanceId), JSON.stringify({ query, nextAsset: setup.nextAsset, snapshotPending: setup.snapshotPending }));
    } else {
      sessionStorage.removeItem(storageKey(instanceId));
    }
    return true;
  } catch (error) {
    console.warn("Could not store device setup progress", error);
    return false;
  }
}

export async function resolveSetupAssets(client: Limrun, setup: DeviceSetup): Promise<Limrun.Asset[]> {
  return Promise.all(setup.configuration.assets.slice(setup.nextAsset).map(async name => {
    const assets = await client.assets.list({
      nameFilter: name, includeAppStore: name.startsWith("appstore/"), includeDownloadUrl: true, limit: 1,
    }, orgRequestOptions(setup.organizationId));
    const asset = assets.find(asset => asset.name === name);
    if (!asset?.signedDownloadUrl) throw new Error(`App "${name}" is unavailable for download.`);
    // Listing can sign a URL before upload; md5 is returned only after a successful storage HEAD.
    if (!asset.md5) throw new Error(`Could not verify the uploaded file for app "${name}". Try again.`);
    if (asset.kind && asset.kind !== "App") throw new Error(`Asset "${name}" is not an app.`);
    if (asset.platform && asset.platform !== setup.configuration.platform) {
      throw new Error(`App "${name}" is not available for ${setup.configuration.platform}.`);
    }
    return asset;
  }));
}

// A checkpoint advances only after installation and launch both succeed. Retrying
// repeats the unfinished app, then continues in order without replaying completed apps.
export async function runDeviceSetup(
  client: Limrun,
  instance: Limrun.IosInstance | Limrun.AndroidInstance,
  setup: DeviceSetup,
  assets: Limrun.Asset[],
  progress: (message: string | null) => void,
  checkpoint: (setup: DeviceSetup) => void,
  active: () => boolean,
): Promise<void> {
  const installed = new Map<string, string>();
  if (setup.snapshotPending) {
    const status = instance.status as typeof instance.status & { deviceState?: DeviceStateStatus };
    const downloadUrl = status.deviceState?.restore.downloadUrl;
    if (downloadUrl === undefined) throw new Error("This server does not support client snapshot restoration yet.");
    progress("Restoring snapshot");
    try {
      await restoreDeviceSnapshot(instance, downloadUrl);
    } catch (error) {
      if (!active()) return;
      if (!(error instanceof LegacySnapshotAppsRequiredError) || !assets.length) throw error;
      // Legacy archives contain data only, so install their apps before retrying restoration.
      for (const asset of assets) {
        if (!active()) return;
        progress(`Installing ${asset.name}`);
        installed.set(asset.name, await installDeviceApp(instance, asset));
      }
      if (!active()) return;
      progress("Restoring snapshot");
      await restoreDeviceSnapshot(instance, downloadUrl);
    }
    if (!active()) return;
    await client.post<void>(`/v1/ios_instances/${encodeURIComponent(instance.metadata.id)}/device_state/ready`, orgRequestOptions(setup.organizationId));
    if (!active()) return;
    setup = { ...setup, snapshotPending: false };
    checkpoint(setup);
  }
  for (const [offset, asset] of assets.entries()) {
    if (!active()) return;
    progress(`Installing ${asset.name}`);
    const bundleId = installed.get(asset.name);
    if (bundleId) {
      progress(`Launching ${asset.name}`);
      await launchDeviceApp(instance, bundleId, setup.configuration.env ?? {});
    } else {
      await installAndLaunchApp(instance, asset, setup.configuration.platform, setup.configuration.env ?? {}, () => {
        if (active()) progress(`Launching ${asset.name}`);
      }, active);
    }
    if (!active()) return;
    checkpoint({ ...setup, nextAsset: setup.nextAsset + offset + 1 });
  }
  if (!active()) return;
  if (setup.configuration.openUrl) {
    progress("Opening URL");
    await openDeviceUrl(instance, setup.configuration.openUrl);
  }
  if (active()) progress(null);
}
